export interface FirestoreTimestampLike {
  seconds: number;
  nanoseconds: number;
  toDate(): Date;
  toMillis(): number;
}

/**
 * Prayer type - can be either prayer request or praise report
 */
export type PrayerType = 'prayer' | 'praise';

/**
 * Prayer privacy level determines who can see the prayer
 */
export type PrivacyLevel = 'public' | 'private' | 'leaders_only';

/**
 * Prayer status - active prayers are current, answered have been resolved
 */
export type PrayerStatus = 'active' | 'answered';

/**
 * Encouragement note left on a prayer
 */
export interface EncouragementNote {
  id: string;
  userId: string;
  userName: string;
  note: string;
  createdAt: FirestoreTimestampLike | string;
}

/**
 * Prayer document from Firestore or Backend API
 */
export interface Prayer {
  id: string;
  organizationId: string;
  groupId: string;
  userId: string;
  userName: string;
  prayerText: string;
  type: PrayerType;
  privacyLevel: PrivacyLevel;
  status: PrayerStatus;
  answerText?: string;
  answeredAt?: FirestoreTimestampLike | string;
  prayedForBy: string[];
  prayedForCount: number;
  encouragementNotes: EncouragementNote[];
  /** Reactions on encouragement notes: { [noteId]: { [emoji]: [firebaseUid, ...] } } */
  encouragementReactions?: Record<string, Record<string, string[]>>;
  createdAt: FirestoreTimestampLike | string;
  updatedAt: FirestoreTimestampLike | string;
  liftedAt?: FirestoreTimestampLike | string;   // When last lifted by author (null = never lifted)
  liftedCount?: number;            // Total times lifted (0 = never)
}

/**
 * Data required to submit a new prayer
 */
export interface SubmitPrayerData {
  prayerText: string;
  type: PrayerType;
  privacyLevel: PrivacyLevel;
  groupId: string;
}

/** Check if a prayer is currently in the "lifted" state (within 48h of last lift) */
export function isPrayerLifted(prayer: Prayer): boolean {
  if (!prayer.liftedAt) return false;
  const liftedTime = typeof prayer.liftedAt === 'string'
    ? new Date(prayer.liftedAt).getTime()
    : (prayer.liftedAt as any).toDate
      ? (prayer.liftedAt as any).toDate().getTime()
      : new Date(prayer.liftedAt as any).getTime();
  const hoursSinceLift = (Date.now() - liftedTime) / (1000 * 60 * 60);
  return hoursSinceLift < 48;
}

/**
 * Filter options for prayer queries
 */
export interface PrayerFilters {
  status?: 'active' | 'answered' | 'all';
  type?: 'prayer' | 'praise' | 'all';
  searchText?: string;
}
